import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Search, Plus, Factory } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MachineCard from "../components/MachineCard";

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [machines, setMachines] = useState(null);
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("todas");

  useEffect(() => {
    Promise.all([api.get("/machines"), api.get("/locations")]).then(([m, l]) => {
      setMachines(m.data);
      setLocations(l.data);
    });
  }, []);

  const filtered = useMemo(() => {
    if (!machines) return [];
    const q = search.trim().toLowerCase();
    return machines.filter((m) => {
      if (locationFilter !== "todas" && m.location_id !== locationFilter) return false;
      if (!q) return true;
      return [m.name, m.manufacturer, m.model, m.serial_number, m.supplier, m.service_application, m.location_name]
        .concat((m.tech_data || []).flatMap((d) => [d.name, d.value]))
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [machines, search, locationFilter]);

  if (machines === null) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-8 w-8 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" />
      </div>
    );
  }

  if (machines.length === 0) {
    return (
      <div className="flex flex-col items-center text-center pt-14 sm:pt-24 animate-fade-up" data-testid="empty-onboarding">
        <span className="h-20 w-20 rounded-3xl bg-zinc-900 text-white flex items-center justify-center mb-8">
          <Factory size={36} strokeWidth={1.5} />
        </span>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter max-w-md">
          Empieza creando el pasaporte digital de tu primera máquina
        </h1>
        <p className="text-zinc-500 mt-4 max-w-sm">
          Centraliza su documentación, fotos e historial. Tardarás menos de 30 segundos.
        </p>
        {isAdmin && (
          <Link
            to="/app/machines/new"
            data-testid="add-first-machine-button"
            className="mt-8 inline-flex items-center gap-2 h-13 px-7 py-3.5 rounded-full bg-zinc-900 text-white font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200"
          >
            <Plus size={18} />
            Añadir primera máquina
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className="animate-fade-up" data-testid="dashboard">
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter">Máquinas</h1>
          <p className="text-zinc-500 text-sm mt-1" data-testid="machine-count">
            {machines.length} {machines.length === 1 ? "máquina" : "máquinas"}
          </p>
        </div>
        {isAdmin && (
          <Link
            to="/app/machines/new"
            data-testid="add-machine-button"
            className="hidden sm:inline-flex items-center gap-2 px-5 py-3 rounded-full bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 active:scale-95 transition-[background-color,transform] duration-200 shrink-0"
          >
            <Plus size={16} />
            Añadir máquina
          </Link>
        )}
      </div>

      <div className="max-w-2xl mx-auto mb-8">
        <div className="relative">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-zinc-400" size={20} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar máquina, fabricante, modelo o número de serie…"
            data-testid="machine-search-input"
            className="w-full h-14 pl-13 pr-5 rounded-full bg-white border border-zinc-200/80 card-shadow text-base sm:text-lg placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-950 focus:ring-offset-2 transition-shadow"
            style={{ paddingLeft: "3.25rem" }}
          />
        </div>
      </div>

      {locations.length > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-8 -mx-5 px-5 sm:mx-0 sm:px-0" data-testid="location-filters">
          <button
            onClick={() => setLocationFilter("todas")}
            data-testid="filter-location-all"
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
              locationFilter === "todas"
                ? "bg-zinc-900 text-white border-zinc-900"
                : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"
            }`}
          >
            Todas
          </button>
          {locations.map((loc) => (
            <button
              key={loc.id}
              onClick={() => setLocationFilter(loc.id)}
              data-testid={`filter-location-${loc.id}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors duration-200 ${
                locationFilter === loc.id
                  ? "bg-zinc-900 text-white border-zinc-900"
                  : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-300"
              }`}
            >
              {loc.name}
            </button>
          ))}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-20" data-testid="no-results">
          <p className="text-zinc-500">Sin resultados para «{search}».</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((m, i) => (
            <MachineCard key={m.id} machine={m} index={i} />
          ))}
        </div>
      )}

      {isAdmin && createPortal(
        <Link
          to="/app/machines/new"
          data-testid="add-machine-fab"
          className="sm:hidden fixed right-5 z-40 inline-flex items-center gap-2 h-14 pl-4 pr-6 rounded-full bg-zinc-900 text-white text-sm font-medium shadow-lg active:scale-95 transition-transform"
          style={{ bottom: "calc(6.5rem + env(safe-area-inset-bottom, 0px))" }}
        >
          <Plus size={20} />
          Añadir máquina
        </Link>,
        document.body
      )}
    </div>
  );
}
